/**
 * AstorStepper — custom horizontal stepper used on the loan detail and
 * new-loan wizard surfaces. 26px circles (default) or 22px (`compact`),
 * mint connectors for completed segments, ring halo on the current step,
 * "In progress" sub-label under the active stage.
 *
 * Per spec Flag #2 we render this locally instead of MUI Stepper — the ring
 * halo + sub-label are hard to express through MUI's `slots` API.
 */

'use client';

import CheckIcon from '@mui/icons-material/Check';
import { Box, baseColors, Typography } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';

export interface AstorStepperProps {
  stages: string[];
  /** Zero-based index of the current (in-progress) stage. -1 for terminal. */
  current: number;
  /** "compact" → 22px circles for wizards; default → 26px for detail pages. */
  size?: 'compact' | 'default';
  /** Localized "In progress" sub-label under the current step. */
  inProgressLabel?: string;
}

export function AstorStepper({ stages, current, size = 'default', inProgressLabel }: AstorStepperProps): ReactElement {
  const circle = size === 'compact' ? 22 : 26;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {stages.map((label, index) => {
        const isDone = current >= 0 && index < current;
        const isHere = current >= 0 && index === current;
        const isFuture = current === -1 || index > current;
        const isLast = index === stages.length - 1;
        const bg = isDone || isHere ? baseColors.teal[500] : 'transparent';
        const border = isFuture ? `1px solid ${baseColors.neutral[300]}` : `1px solid ${baseColors.teal[500]}`;
        const textColor = isDone || isHere ? baseColors.neutral[0] : 'text.secondary';
        return (
          <Box
            key={label}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              minWidth: 0,
            }}
          >
            {/* Connector — drawn to the right of every circle except the last */}
            {!isLast && (
              <Box
                sx={{
                  position: 'absolute',
                  top: circle / 2 - 1,
                  left: '50%',
                  right: `-${50}%`,
                  height: 2,
                  bgcolor: isDone ? baseColors.teal[500] : baseColors.neutral[200],
                  zIndex: 0,
                }}
              />
            )}
            {/* Circle */}
            <Box
              sx={{
                width: circle,
                height: circle,
                borderRadius: '50%',
                border,
                bgcolor: bg,
                color: textColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1,
                boxShadow: isHere ? `0 0 0 4px ${baseColors.teal[100]}` : 'none',
                transition: 'box-shadow 200ms ease',
              }}
            >
              {isDone ? (
                <CheckIcon sx={{ fontSize: circle * 0.55 }} />
              ) : (
                // Compact (18px circle) → overline (11px); default (24px+) →
                // caption (13px). Lets the type scale carry the size instead
                // of an inline fontSize override.
                <Typography
                  variant={size === 'compact' ? 'overline' : 'caption'}
                  sx={{ fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1 }}
                >
                  {index + 1}
                </Typography>
              )}
            </Box>
            {/* Label */}
            <Typography
              variant="caption"
              sx={{
                mt: 0.75,
                fontWeight: isHere ? 600 : 400,
                color: isHere ? 'text.primary' : 'text.secondary',
                textAlign: 'center',
                lineHeight: 1.3,
                px: 0.5,
              }}
            >
              {label}
            </Typography>
            {/* Sub-label under current step */}
            {isHere && inProgressLabel && (
              <Typography variant="caption" sx={{ mt: 0.25, color: baseColors.teal[600], fontWeight: 500 }}>
                {inProgressLabel}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
